import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { SandboxedWebComponent } from '../../../../../../../../../../standalone/web-components';
import { CUSTOM_COMPONENT_DATA } from '../../../../custom-components/custom-component-data.provider';
import { IDataSourceSettings } from '../../../../data-source/interfaces/data-source-settings.interface';
import { DataSourceSettingsRef } from '../../../../data-source/interfaces/data-source-settings.token';
import { FiltersChannel } from '../../../../filters/models/filters-channel.model';
import { ICustomComponentTileDefinition } from '../../../interfaces/tiles/definitions/custom-component-tile-definition.interface';

@Component({
  standalone: true,
  selector: 'user-custom-component-tile',
  imports: [SandboxedWebComponent],
  template: `<sandboxed-web-component [data]="customComponentData()" [src]="src()" />`,
})
export class UserCustomComponentTile {
  private readonly dataSourceSettings = inject(
    DataSourceSettingsRef,
  ) as unknown as IDataSourceSettings;

  private readonly baseCustomComponentData = inject(CUSTOM_COMPONENT_DATA) as {
    message: string;
  };

  readonly tileInstance = input.required<ICustomComponentTileDefinition>();

  readonly filtersChannel = input<FiltersChannel>();

  private readonly filtersSnapshot = signal<unknown>(null);

  private readonly filtersVersion = signal(0);

  constructor() {
    effect(
      (onCleanup) => {
        const channel = this.filtersChannel();

        if (!channel) {
          this.filtersSnapshot.set(null);
          this.filtersVersion.set(0);
          return;
        }

        const refreshFilters = () => {
          const nextFilters = this.getActiveFilters(channel);
          this.filtersSnapshot.set(this.cloneSerializable(nextFilters));
          this.filtersVersion.update((value) => value + 1);
        };

        refreshFilters();

        const subscription = new Subscription();
        subscription.add(channel.selectedFiltersSubject.subscribe(() => refreshFilters()));
        subscription.add(channel.filters.subscribe(() => refreshFilters()));

        onCleanup(() => subscription.unsubscribe());
      },
      { allowSignalWrites: true },
    );
  }

  protected readonly customComponentData = computed(() => ({
    ...this.baseCustomComponentData,
    filters: this.filtersSnapshot(),
    filtersQuery: (this.filtersSnapshot() as { query?: unknown } | null)?.query ?? null,
    filtersVersion: this.filtersVersion(),
  }));

  readonly src = computed(
    () =>
      `${this.dataSourceSettings.dataServiceApi}custom-component/${this.tileInstance().customComponentId}/module.mjs`,
  );

  private getActiveFilters(channel: FiltersChannel): unknown {
    return channel.filters?.getValue?.() ?? channel.selectedFilters ?? null;
  }

  private cloneSerializable<T>(value: T): T {
    if (value === null || value === undefined) {
      return value;
    }

    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }
}
